import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {UtilService} from '../../../services/util/util.service';
import {ValidationService} from '../../../services/validation/validation.service';
import {debounceTime, Subject, Subscription} from 'rxjs';
import {DirtyStateTrackerService} from "../../../services/dirty-state-tracker/dirty-state-tracker.service";
import {ViewService} from "../../../services/view/view.service";

export type TextInputType = 'text' | 'email' | 'link' | 'textarea';
export const TextInputType: {Text: TextInputType, Email: TextInputType, Link: TextInputType, Textarea: TextInputType} = {
  Text: 'text' as TextInputType,
  Email: 'email' as TextInputType,
  Link: 'link' as TextInputType,
  Textarea: 'textarea' as TextInputType
};

@Component({
  selector: 'app-text-input',
  templateUrl: './text-input.component.html',
  styleUrls: ['./text-input.component.scss']
})
export class TextInputComponent implements OnInit, OnDestroy {
  @Input() public label: string;
  @Input() public placeholder: string;
  @Input() public min: number;
  @Input() public appClass: string | string[] | Set<string> | {[p: string]: any};
  @Input() public withPrefix: boolean;
  @Input() public withSuffix: boolean;
  @Input() public errorMessage: string;
  @Input() public name: string;
  @Input() public minRows: number = 3;
  @Input() public maxRows: number = 5;
  @Input() public required: boolean;
  @Input() public disabled: boolean;
  @Input() public type: TextInputType = TextInputType.Text;
  @Input() public hint: string;
  @Input() public hideHints: boolean;
  @Input() public readonly: boolean;
  @Input() public small: boolean;

  private _max: number;
  @Input() public set max(value: number) {
    this._max = value;
  }
  public get max(): number {
    return this._max || (this.type === TextInputType.Email ? ValidationService.MAX_EMAIL_LENGTH : null);
  }

  // For custom validations
  _hasCustomError: boolean;
  @Input() public set hasCustomError(value: boolean) {
    this._hasCustomError = value;
    this.viewService.triggerCdr();
  }
  public get hasCustomError(): boolean {
    return this._hasCustomError;
  }
  @Input() public customErrorFunction: Function | boolean;
  @Input() public customError: string;

  private _value: string;
  public get value(): string {
    return this._value;
  }
  @Input() public set value(newValue: string) {
    if ((newValue?.length || this._value?.length) && newValue !== this._value) {
      this._value = newValue;
      if (this._firstSet) {
        this.changeValue();
      }
    }
    this._firstSet = true;
  }
  @Output() public valueChange: EventEmitter<string> = new EventEmitter<string>();
  @Output() public inputKeyup: EventEmitter<KeyboardEvent> = new EventEmitter<KeyboardEvent>();

  @HostBinding('class') get hostClasses(): string {
    return (this.withPrefix || this.withSuffix) ? 'flex flex-flat' : '';
  }

  public isInputDirty: boolean;
  public isInputTouched: boolean;
  @Input() public dirtyParent: string;
  @Input() public dirtyService: DirtyStateTrackerService;
  @Input() public set dirtyTracker(value: any) {
    if (this.dirtyService) {
      this.isInputDirty = this.dirtyService.isParentDirty(this.isInputDirty, this.dirtyParent);
    }
  }

  public validations: {valid: boolean, tooLong: boolean, tooShort: boolean, emailInvalid: boolean, linkInvalid: boolean} = {
    valid: true,
    tooLong: false,
    tooShort: false,
    emailInvalid: false,
    linkInvalid: false
  };
  private _debounceSub: Subscription;
  private _textDebounce: Subject<string> = new Subject<string>();
  private _firstSet: boolean;

  constructor(public utilService: UtilService,
              private viewService: ViewService) {}

  public ngOnInit(): void {
    this.validateValue();
    this._debounceSub = this._textDebounce.pipe(debounceTime(500)).subscribe(_ => {
      this.validateValue();
    });
  }
  public ngOnDestroy(): void {
    if (this._debounceSub) {
      this._debounceSub.unsubscribe();
      delete this._debounceSub;
    }
  }

  public get isEmail(): boolean {
    return this.type === TextInputType.Email;
  }
  public get isTextarea(): boolean {
    return this.type === TextInputType.Textarea;
  }
  public get isLink(): boolean {
    return this.type === TextInputType.Link;
  }
  public get isValid(): boolean {
    return !!Object.values(this.validations).filter(v => !v).length && !this.hasCustomError;
  }

  public validateValue(): void {
    this.hasCustomError = false;
    if (this.customErrorFunction && typeof this.customErrorFunction !== undefined) {
      if (typeof this.customErrorFunction === 'function') {
        this.hasCustomError = this.customErrorFunction(this.value);
      } else {
        this.hasCustomError = this.customErrorFunction;
      }
    }
    this.validations.tooLong = this.max && this.value?.length > this.max;
    this.validations.tooShort = this.min && this.value?.length < this.min;
    this.validations.emailInvalid = this.isEmail && !ValidationService.validateEmail(this.value);
    this.validations.linkInvalid = this.isLink && !ValidationService.validateUrl(this.value);
    this.validations.valid = !this.value?.length || (!this.hasCustomError && !this.validations.tooLong && !this.validations.tooShort &&
      !this.validations.emailInvalid && !this.validations.linkInvalid);
    this.viewService.triggerCdr();
  }

  public get requiredButEmpty(): boolean {
    return (this.isInputDirty || this.isInputTouched) && this.required && !this.value?.length;
  }

  public changeValue(): void {
    this.valueChange.emit(this._value);
    this._textDebounce.next(this._value);
    this.isInputDirty = true;
  }
}
